import SupplierContext from "@contexts/SupplierContext";
import { useContext, useMemo } from "react";
import IOwTableCols from "@models/IOwTableCols";
import IQBD_QueueUI from "@models/IQBD_QueueUI";
import HelperService from "@services/HelperService";
import UserService from "@services/UserService";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";

export const useQbdLogViewerColumns = () => {
    const supplierModel = useContext(SupplierContext);

    const columns = useMemo(() => {
        const compiledColumns: IOwTableCols[] = [];

        const qbdTypeCol: IOwTableCols = {
            title: "type",
            valueProp: "qbd_type",
            translateValue: true,
        };

        compiledColumns.push(qbdTypeCol);

        const qbdActionCol: IOwTableCols = {
            title: "action",
            valueProp: "qbd_action",
            translateValue: true,
        };

        compiledColumns.push(qbdActionCol);

        const orderNameDetailCol: IOwTableCols = {
            title: "order",
            valueProp: "order_name",
            className: "no-wrap",
        };

        compiledColumns.push(orderNameDetailCol);

        const vendorNameDetailCol: IOwTableCols = {
            title: "vendor",
            valueProp: "vendor_name",
            isVendorOverride: true,
        };

        compiledColumns.push(vendorNameDetailCol);

        const catalogItemNameDetailCol: IOwTableCols = {
            title: "catalog_item",
            valueProp: "catalog_item_name",
        };

        compiledColumns.push(catalogItemNameDetailCol);

        if (supplierModel.has_purchase_orders) {
            const purchaseOrderNameDetailCol: IOwTableCols = {
                title: "purchase_order",
                valueProp: "po_name",
            };

            compiledColumns.push(purchaseOrderNameDetailCol);
        }

        const qbdStatusCol: IOwTableCols = {
            title: "status",
            valueProp: "qbd_status",
            translateValue: true,
        };

        compiledColumns.push(qbdStatusCol);

        const hasErrorDetailsCol: IOwTableCols = {
            title: "error",
            valueProp: "has_error_details",
            renderFnEmitsRow: true,
            renderFn: (row: IQBD_QueueUI) => {
                return row?.has_error_details || row?.qbd_error || row?.qbd_error_message ? (
                    <div>
                        <i className={"text-danger fad fa-times-circle"} />
                        {row?.qbd_error_message && (
                            <OwIconTooltip tooltipContent={row?.qbd_error_message} iconClassName="fad fa-info-circle ms-2" />
                        )}
                    </div>
                ) : (
                    ""
                );
            },
        };

        compiledColumns.push(hasErrorDetailsCol);

        const hasProcessedResponseCol: IOwTableCols = {
            title: "processed",
            valueProp: "has_processed_response",
            renderFnEmitsRow: true,
            renderFn: (row: IQBD_QueueUI) => {
                return row?.has_processed_response ? <i className={"text-success fad fa-check-circle"} /> : "";
            },
        };

        compiledColumns.push(hasProcessedResponseCol);

        const hasResponseCol: IOwTableCols = {
            title: "response",
            valueProp: "has_response",
            renderFnEmitsRow: true,
            renderFn: (row: IQBD_QueueUI) => {
                return row?.has_response ? <i className={"text-success fad fa-check-circle"} /> : "";
            },
        };

        compiledColumns.push(hasResponseCol);

        const createdCol: IOwTableCols = {
            title: "created",
            valueProp: "created_at",
            renderFnEmitsRow: true,
            renderFn: (row: IQBD_QueueUI) => {
                return row?.created_at ? UserService.parseLastUpdated(row.created_at) : "";
            },
        };

        compiledColumns.push(createdCol);

        return compiledColumns;
    }, []);

    return columns;
};
